import { useI18n } from '@ecom/i18n';
import {
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@ecom/ui-kit';
import { type ReactElement } from 'react';

import { KIND_LABEL_KEY, type ProvisionalPassword } from '../types';

/**
 * Accounts that still hold a provisional password: who, until when, and which ones ran out (to be renewed by whoever handed them over).
 * Read only. Nothing about the password itself is ever returned by the API: only that one exists and when it stops working.
 */
export function ProvisionalPasswordsTable({
  accounts,
}: {
  readonly accounts: readonly ProvisionalPassword[];
}): ReactElement {
  const { t } = useI18n();
  return (
    <Table caption={t('admin.access.provisional.caption')}>
      <TableHead>
        <TableRow>
          <TableHeaderCell>{t('admin.access.col.account')}</TableHeaderCell>
          <TableHeaderCell>{t('admin.access.col.kind')}</TableHeaderCell>
          <TableHeaderCell>{t('admin.access.col.expires')}</TableHeaderCell>
          <TableHeaderCell>{t('admin.access.lookup.status')}</TableHeaderCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {accounts.map((account) => (
          <TableRow key={account.userId}>
            <TableCell>
              <span className="text-sm text-ink">{account.displayName ?? account.email}</span>
              <p className="text-xs text-inkFaint">{account.email}</p>
            </TableCell>
            <TableCell>
              <Badge tone={account.kind === 'superadmin' ? 'accent' : 'neutral'}>
                {t(KIND_LABEL_KEY[account.kind])}
              </Badge>
            </TableCell>
            <TableCell>
              <span className="text-xs text-inkFaint">
                {new Date(account.expiresAt).toLocaleString('fr-TN')}
              </span>
            </TableCell>
            <TableCell>
              <Badge tone={account.expired ? 'critical' : 'warn'}>
                {t(
                  account.expired
                    ? 'admin.access.provisional.expired'
                    : 'admin.access.provisional.valid',
                )}
              </Badge>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
